import { WebSocketService } from '../core/WebSocketService.js';
import {
	applyliveChatCustomStyles,
	removeliveChatCustomStyles,
} from '../styles/liveChatCustomStyles.js';
import { NotificationSound } from '../utils/NotificationSound.js';
import { isConversationClosedError } from '../utils/errors.js';
import { BaseWidget } from './BaseWidget.js';
import { LiveChatState } from './liveChat/LiveChatState.js';
import { LiveChatLauncher } from './liveChat/components/LiveChatLauncher.js';
import { LiveChatPanel } from './liveChat/components/LiveChatPanel.js';
import { ChangelogView } from './liveChat/views/ChangelogView.js';
import { ChatView } from './liveChat/views/ChatView.js';
import { ConversationsView } from './liveChat/views/ConversationsView.js';
import { HelpView } from './liveChat/views/HelpView.js';
import { HomeView } from './liveChat/views/HomeView.js';
import { FeedbackFormView } from './liveChat/views/FeedbackFormView.js';
import { PreChatFormView } from './liveChat/views/PreChatFormView.js';

export class LiveChatWidget extends BaseWidget {
	constructor(options) {
		super({ ...options, type: 'liveChat' });
		this._explicitOptions = options._explicitOptions || {};
		const resolvedTheme = options.theme || 'light';
		const hasExplicitTextColor = Object.prototype.hasOwnProperty.call(
			options,
			'textColor'
		);
		const hasExplicitBackgroundColor = Object.prototype.hasOwnProperty.call(
			options,
			'backgroundColor'
		);
		const defaultTextColor = resolvedTheme === 'dark' ? '#E2E8F0' : '#1d1d1f';
		const defaultBackgroundColor =
			resolvedTheme === 'dark' ? '#0F172A' : '#ffffff';

		const resolvedEnableChangelog =
			typeof options.enableChangelog === 'boolean'
				? options.enableChangelog
				: options.enableNews !== false;

		this.liveChatOptions = {
			position: options.position || 'right',
			theme: resolvedTheme,
			primaryColor: options.primaryColor || '#155EEF',
			textColor: hasExplicitTextColor ? options.textColor : defaultTextColor,
			backgroundColor: hasExplicitBackgroundColor
				? options.backgroundColor
				: defaultBackgroundColor,
			logoUrl: options.logoUrl || 'https://product7.io/p7logo.svg',
			teamName: options.teamName || 'Support',
			teamAvatars: options.teamAvatars || [],
			greetingMessage: options.greetingMessage || 'Hi there 👋',
			welcomeMessage: options.welcomeMessage || 'How can we help?',
			onlineMessage: options.onlineMessage || "We're online now",
			responseTime:
				options.responseTime || 'We typically reply within a few minutes',
			enableHelp: options.enableHelp !== false,
			enableChangelog: resolvedEnableChangelog,
			autoLoadData: options.autoLoadData !== false,
			initialView: options.initialView || 'home',
			previewData: options.previewData || null,
			featuredContent: options.featuredContent || null,
			feedbackBoardName: options.feedbackBoardName || null,
			feedbackUrl: options.feedbackUrl || null,
			changelogUrl: options.changelogUrl || null,
			helpUrl: options.helpUrl || null,
			roadmapUrl: options.roadmapUrl || null,
			composePlaceholder: options.composePlaceholder || null,
			onSendMessage: options.onSendMessage || null,
			onFeedbackClick: options.onFeedbackClick || null,
			onArticleClick: options.onArticleClick || null,
			onChangelogClick: options.onChangelogClick || null,
		};

		const sdkMetadata = this.sdk?.apiService?.getMetadata() || null;

		this.LiveChatState = new LiveChatState({
			teamName: this.liveChatOptions.teamName,
			teamAvatars: this.liveChatOptions.teamAvatars,
			greetingMessage: this.liveChatOptions.greetingMessage,
			welcomeMessage: this.liveChatOptions.welcomeMessage,
			onlineMessage: this.liveChatOptions.onlineMessage,
			responseTime: this.liveChatOptions.responseTime,
			enableHelp: this.liveChatOptions.enableHelp,
			enableChangelog: this.liveChatOptions.enableChangelog,
			metadata: sdkMetadata,
			urls: {
				feedback: this.liveChatOptions.feedbackUrl,
				changelog: this.liveChatOptions.changelogUrl,
				help: this.liveChatOptions.helpUrl,
				roadmap: this.liveChatOptions.roadmapUrl,
			},
		});

		// If identify() was called before this widget was created, pre-seed identity
		if (this.sdk?.identified && sdkMetadata) {
			this.LiveChatState.isIdentified = true;
		}
		this.LiveChatState.setBlockState(this.apiService?.getBlockState?.());

		this.launcher = null;
		this.panel = null;
		this.wsService = null;
		this._wsUnsubscribers = [];
		this._feedbackWidget = null;

		const notificationSoundOption =
			options.notificationSound !== undefined
				? options.notificationSound
				: true;

		this._notificationSound = new NotificationSound({
			soundUrl:
				typeof notificationSoundOption === 'string'
					? notificationSoundOption
					: undefined,
			volume: options.notificationVolume ?? 0.4,
		});

		if (notificationSoundOption === false) {
			this._notificationSound.setEnabled(false);
		}

		this._handleOpenChange = this._handleOpenChange.bind(this);
		this._handleWebSocketMessage = this._handleWebSocketMessage.bind(this);
		this._handleTypingStarted = this._handleTypingStarted.bind(this);
		this._handleTypingStopped = this._handleTypingStopped.bind(this);
		this._handleAiComposingStarted = this._handleAiComposingStarted.bind(this);
		this._handleAiComposingStopped = this._handleAiComposingStopped.bind(this);
		this._handleConversationClosed = this._handleConversationClosed.bind(this);
	}

	_hasTrigger() {
		return this.options.trigger === true || this.options.trigger === undefined;
	}

	_createInternalFeedbackWidget() {
		try {
			const widget = this.sdk.createWidget('button', {
				trigger: false,
				displayMode: 'modal',
				boardName:
					this.liveChatOptions.feedbackBoardName || this.sdk.config.boardName,
				primaryColor: this.liveChatOptions.primaryColor,
				theme: this.liveChatOptions.theme,
			});
			widget.mount();
			return widget;
		} catch (e) {
			console.warn('[LiveChat] Could not create internal feedback widget:', e);
			return null;
		}
	}

	async _handleSubmitFeedback({ title, message }) {
		const payload = {
			title: title || 'Feedback',
			content: message,
			board_id:
				this.liveChatOptions.feedbackBoardName || this.sdk.config.boardName,
		};

		const response = await this.apiService.submitFeedback(payload);

		this.sdk.eventBus.emit('feedback:submitted', {
			widget: this,
			feedback: response,
		});

		return response;
	}

	_render() {
		const container = document.createElement('div');
		container.className = `liveChat-widget theme-${this.liveChatOptions.theme}`;
		container.style.zIndex = '999999';
		container.style.setProperty(
			'--liveChat-h-padding',
			`${this.liveChatOptions.horizontalPadding ?? 20}px`
		);
		container.style.setProperty(
			'--liveChat-v-padding',
			`${this.liveChatOptions.verticalPadding ?? 20}px`
		);
		this._widgetContainer = container;

		applyliveChatCustomStyles({
			primaryColor: this.liveChatOptions.primaryColor,
			textColor: this.liveChatOptions.textColor,
			backgroundColor: this.liveChatOptions.backgroundColor,
			theme: this.liveChatOptions.theme,
		});

		// Create internal feedback widget (no floating button) so the
		// "Leave a feedback" button in the home view can open it inline.
		if (!this.liveChatOptions.onFeedbackClick) {
			this._feedbackWidget = this._createInternalFeedbackWidget();
		}

		if (this._hasTrigger()) {
			this.launcher = new LiveChatLauncher(this.LiveChatState, {
				position: this.liveChatOptions.position,
				primaryColor: this.liveChatOptions.primaryColor,
			});
			container.appendChild(this.launcher.render());
		}

		this.panel = new LiveChatPanel(this.LiveChatState, {
			position: this.liveChatOptions.position,
			theme: this.liveChatOptions.theme,
			primaryColor: this.liveChatOptions.primaryColor,
			logoUrl: this.liveChatOptions.logoUrl,
			featuredContent: this.liveChatOptions.featuredContent,
			onSendMessage:
				this.liveChatOptions.onSendMessage ||
				this._handleSendMessage.bind(this),
			onStartConversation: this._handleStartConversation.bind(this),
			onTyping: this.sendTypingIndicator.bind(this),
			onSelectConversation: this._handleSelectConversation.bind(this),
			onStartNewConversation: this._handleNewConversationClick.bind(this),
			onIdentifyContact: this._handleIdentifyContact.bind(this),
			onFeedbackClick:
				this.liveChatOptions.onFeedbackClick ||
				(this._feedbackWidget ? () => this._feedbackWidget.open() : null),
			onSubmitFeedback: this._handleSubmitFeedback.bind(this),
			onArticleClick: this.liveChatOptions.onArticleClick,
			onChangelogClick: this.liveChatOptions.onChangelogClick,
		});

		this.panel.registerView('home', HomeView);
		this.panel.registerView('messages', ConversationsView);
		this.panel.registerView('chat', ChatView);
		this.panel.registerView('prechat', PreChatFormView);
		this.panel.registerView('feedback', FeedbackFormView);
		this.panel.registerView('help', HelpView);
		this.panel.registerView('changelog', ChangelogView);

		container.appendChild(this.panel.render());
		this.panel.hide();

		return container;
	}

	_attachEvents() {
		this._stateUnsubscribe = this.LiveChatState.subscribe((type, data) => {
			if (type === 'openChange') {
				this._handleOpenChange(data.isOpen);
			}
			if (type === 'conversationChange') {
				this._handleActiveConversationChange(
					data.conversationId,
					data.previousConversationId
				);
			}
		});
	}

	_handleOpenChange(isOpen) {
		if (isOpen) {
			this.panel.show();
			this.sdk.eventBus.emit('liveChat:opened', { widget: this });
		} else {
			this.panel.hide();
			this.LiveChatState.reset();
			this.sdk.eventBus.emit('liveChat:closed', { widget: this });
		}
	}

	_handleActiveConversationChange(conversationId, previousConversationId) {
		if (previousConversationId && this.wsService) {
			this.wsService.send('conversation:unsubscribe', {
				conversation_id: previousConversationId,
			});
		}
		if (conversationId && this.wsService) {
			this.wsService.send('conversation:subscribe', {
				conversation_id: conversationId,
			});
		}
	}

	async _handleStartConversation(messageContent, pendingAttachments) {
		try {
			if (!this.LiveChatState.isIdentified) {
				this.LiveChatState.pendingMessage = {
					content: messageContent,
					attachments: pendingAttachments,
				};
				this.LiveChatState.setView('prechat');
				return null;
			}

			const openConversation = this.LiveChatState.conversations.find(
				(c) => c.status === 'open'
			);

			if (openConversation) {
				this.LiveChatState.setActiveConversation(openConversation.id);
				await this._handleSendMessage(
					openConversation.id,
					{ content: messageContent },
					pendingAttachments
				);
				return openConversation;
			}

			return await this.startNewConversation(
				messageContent,
				'',
				pendingAttachments
			);
		} catch (error) {
			console.error('[LiveChatWidget] Failed to start conversation:', error);
			return null;
		}
	}

	async _handleSelectConversation(conversationId) {
		try {
			await this.fetchMessages(conversationId);
		} catch (error) {
			console.error('[LiveChatWidget] Failed to fetch messages:', error);
		}
	}

	_handleNewConversationClick() {
		const openConversation = this.LiveChatState.conversations.find(
			(c) => c.status === 'open'
		);

		if (openConversation) {
			this.LiveChatState.setActiveConversation(openConversation.id);
			this.LiveChatState.setView('chat');
			this._handleSelectConversation(openConversation.id);
		} else {
			this.LiveChatState.setActiveConversation(null);
			this.LiveChatState.setView('chat');
		}
	}

	async _handleIdentifyContact(contactData) {
		try {
			// Route through sdk.identify() so the SDK-level identity state is updated
			// and applyIdentity() handles the live chat state + WebSocket as a side effect.
			const result = await this.sdk.identify({
				email: contactData.email,
				name: contactData.name,
			});
			return result;
		} catch (error) {
			console.error('[LiveChatWidget] Failed to identify contact:', error);
			throw error;
		}
	}

	markAsIdentified(name, email) {
		// Called externally by the app when the user is already known.
		// No API call needed — identity was already established via sdk.identify().
		this.applyIdentity({ name, email });
	}

	applyIdentity(metadata = {}) {
		this.LiveChatState.setIdentified(true, metadata);

		if (this.apiService?.sessionToken && !this.wsService?.isConnected) {
			this._initWebSocket();
		}
	}

	applyBlockState(blockState) {
		this.LiveChatState.setBlockState(blockState);
	}

	async _handleUploadFile(base64Data, filename) {
		try {
			const response = await this.apiService.uploadFile(base64Data, filename);
			if (response.status && response.url) {
				return response.url;
			}
			throw new Error('Upload failed');
		} catch (error) {
			console.error('[LiveChatWidget] Failed to upload file:', error);
			throw error;
		}
	}

	async _uploadPendingAttachments(pendingAttachments) {
		if (!pendingAttachments || pendingAttachments.length === 0) return [];

		const uploaded = [];
		for (const att of pendingAttachments) {
			try {
				const cdnUrl = await this._handleUploadFile(att.preview, att.file.name);
				uploaded.push({
					url: cdnUrl,
					type: att.type.startsWith('image') ? 'image' : 'file',
					name: att.file.name,
				});
			} catch (err) {
				console.error(
					'[LiveChatWidget] Skipping failed attachment upload:',
					att.file.name,
					err
				);
			}
		}
		return uploaded;
	}

	async _handleSendMessage(conversationId, message, pendingAttachments) {
		this.sdk.eventBus.emit('liveChat:messageSent', {
			widget: this,
			conversationId,
			message,
		});

		try {
			const uploadedAttachments =
				await this._uploadPendingAttachments(pendingAttachments);

			const response = await this.apiService.sendMessage(conversationId, {
				content: message.content,
				attachments: uploadedAttachments,
			});

			if (response.status && response.data) {
				// Reconcile the optimistic 'Sending…' message with the server's
				// confirmation as soon as the POST returns. Don't depend on the WS
				// echo alone — when the WebSocket frame is dropped (brief
				// disconnect, slow network, server send-buffer full), the
				// optimistic message stays stuck on 'Sending…' forever because
				// upsertMessage's match window is finite.
				const serverData = response.data;
				let attachments = [];
				if (serverData.attachments) {
					try {
						attachments =
							typeof serverData.attachments === 'string'
								? JSON.parse(serverData.attachments)
								: serverData.attachments;
					} catch (e) {
						attachments = [];
					}
				}
				this.LiveChatState.upsertMessage(
					conversationId,
					{
						id: serverData.id,
						content: serverData.content,
						isOwn: true,
						timestamp: serverData.created_at,
						attachments:
							Array.isArray(attachments) && attachments.length > 0
								? attachments
								: undefined,
					},
					{ reconcileOwnOptimistic: true, optimisticMatchWindowMs: 60000 }
				);
			}

			if (this.apiService?.mock) {
				setTimeout(() => {
					const mockResponse = {
						id: 'msg_' + Date.now(),
						content: "Thanks for your message! We'll get back to you soon.",
						isOwn: false,
						timestamp: new Date().toISOString(),
						sender: {
							name: 'Support Team',
							avatarUrl: null,
						},
					};
					this.LiveChatState.addMessage(conversationId, mockResponse);
				}, 1500);
			}
		} catch (error) {
			console.error('[LiveChatWidget] Failed to send message:', error);
			// Mark the optimistic message as failed so the UI can show a
			// retry affordance instead of leaving the user with a silent
			// "message stuck as pending" state.
			if (message?.id) {
				this.LiveChatState.markMessageFailed(conversationId, message.id);
			}
			// The conversation was resolved while this view still showed it
			// open — switch to the resolved state so the composer gives way
			// to the "This conversation has been resolved" notice.
			if (isConversationClosedError(error)) {
				this.LiveChatState.updateConversation(conversationId, {
					status: 'closed',
				});
			}
		}
	}

	_handleWebSocketMessage(data) {
		const { conversation_id, message } = data;

		let attachments = [];
		if (message.attachments) {
			try {
				attachments =
					typeof message.attachments === 'string'
						? JSON.parse(message.attachments)
						: message.attachments;
			} catch (e) {
				// ignore
			}
		}

		const localMessage = {
			id: message.id,
			content: message.content,
			isOwn: message.sender_type === 'customer',
			isSystem: message.sender_type === 'system',
			timestamp: message.created_at,
			attachments: attachments.length > 0 ? attachments : undefined,
			sender: {
				name: message.sender_name || 'Support',
				avatarUrl: message.sender_avatar || null,
			},
		};

		this.LiveChatState.upsertMessage(conversation_id, localMessage, {
			reconcileOwnOptimistic: true,
			optimisticMatchWindowMs: 30000,
		});

		const isOwnMessage = message.sender_type === 'customer';

		// A real reply cancels any AI-composing indicator (bot finished
		// generating, or an agent replied and superseded).
		if (!isOwnMessage) {
			this.LiveChatState._notify('typingStopped', {
				conversationId: conversation_id,
			});
		}

		if (!isOwnMessage && this.LiveChatState.isIdentified) {
			this._notificationSound.play();
		}

		if (
			!this.LiveChatState.isOpen ||
			this.LiveChatState.activeConversationId !== conversation_id
		) {
			this._updateUnreadCount();
		}
	}

	_handleTypingStarted(data) {
		if (data.is_agent) {
			this.LiveChatState._notify('typingStarted', {
				conversationId: data.conversation_id,
				userName: data.user_name,
				isAi: false,
			});
		}
	}

	// AI/bot workflows don't emit the same typing:started events humans do,
	// so the backend fires a distinct ai:composing_started event when the
	// assistant begins generating a reply. Same visual as typing but with
	// an "Assistant" (or configured name) label instead of a human name.
	_handleAiComposingStarted(data) {
		this.LiveChatState._notify('typingStarted', {
			conversationId: data.conversation_id,
			userName: data.assistant_name || data.user_name || 'Assistant',
			isAi: true,
		});
	}

	_handleAiComposingStopped(data) {
		this.LiveChatState._notify('typingStopped', {
			conversationId: data.conversation_id,
		});
	}

	_handleTypingStopped(data) {
		this.LiveChatState._notify('typingStopped', {
			conversationId: data.conversation_id,
		});
	}

	_handleConversationClosed(data) {
		const conversationId =
			data?.conversation_id || data?.id || data?.conversation?.id;
		if (!conversationId) return;

		this.LiveChatState.updateConversation(conversationId, {
			status: 'closed',
		});
	}

	async _updateUnreadCount() {
		try {
			const response = await this.apiService.getUnreadCount();
			if (response.status && response.data) {
				this.LiveChatState.unreadCount = response.data.unread_count || 0;
				this.LiveChatState._notify('unreadCountChange', {
					count: this.LiveChatState.unreadCount,
				});
			}
		} catch (error) {
			console.error('[LiveChatWidget] Failed to get unread count:', error);
		}
	}

	_initWebSocket() {
		if (this.wsService) {
			this.wsService.disconnect();
		}

		this.wsService = new WebSocketService({
			baseURL: this.apiService.baseURL,
			workspace: this.apiService.workspace,
			sessionToken: this.apiService.sessionToken,
			mock: this.apiService.mock,
		});

		this._wsUnsubscribers.push(
			this.wsService.on('message', this._handleWebSocketMessage)
		);
		this._wsUnsubscribers.push(
			this.wsService.on('typing_started', this._handleTypingStarted)
		);
		this._wsUnsubscribers.push(
			this.wsService.on('typing_stopped', this._handleTypingStopped)
		);
		this._wsUnsubscribers.push(
			this.wsService.on('ai_composing_started', this._handleAiComposingStarted)
		);
		this._wsUnsubscribers.push(
			this.wsService.on('ai_composing_stopped', this._handleAiComposingStopped)
		);
		this._wsUnsubscribers.push(
			this.wsService.on('conversation_closed', this._handleConversationClosed)
		);
		// Track first vs reconnect locally so we only backfill on reconnects.
		// First connect is right after _initWebSocket() and the surrounding
		// flow has already loaded fresh data — re-fetching would be wasted.
		let wsHasConnectedBefore = false;
		this._wsUnsubscribers.push(
			this.wsService.on('connected', () => {
				console.log('[LiveChatWidget] WebSocket connected');
				const isReconnect = wsHasConnectedBefore;
				wsHasConnectedBefore = true;
				if (this.LiveChatState.activeConversationId) {
					this.wsService.send('conversation:subscribe', {
						conversation_id: this.LiveChatState.activeConversationId,
					});
					// On reconnect, refetch the active conversation's messages.
					// The server doesn't replay events that fired during the WS
					// disconnect, so anything broadcast in the gap is otherwise
					// permanently lost from the customer's view until they
					// refresh the page.
					if (isReconnect) {
						this.fetchMessages(this.LiveChatState.activeConversationId).catch(
							(err) => {
								console.error(
									'[LiveChatWidget] Failed to backfill messages on reconnect:',
									err
								);
							}
						);
					}
				}
			})
		);
		this._wsUnsubscribers.push(
			this.wsService.on('disconnected', () => {
				console.log('[LiveChatWidget] WebSocket disconnected');
				this.LiveChatState._notify('connectionChange', { connected: false });
			})
		);
		this._wsUnsubscribers.push(
			this.wsService.on('connected', () => {
				this.LiveChatState._notify('connectionChange', { connected: true });
			})
		);
		this._wsUnsubscribers.push(
			this.wsService.on('connectionStateChange', ({ state }) => {
				this.LiveChatState.setConnectionState(state);
			})
		);
		this._wsUnsubscribers.push(
			this.wsService.on('auth_expired', () => {
				console.warn(
					'[LiveChatWidget] WebSocket auth expired; refreshing session'
				);
				// Kick the API layer to re-ensure a session, then rebuild the WS
				// with the new token. Guard against re-entry while a refresh is
				// already in flight.
				if (this._refreshingSession) return;
				this._refreshingSession = true;
				this.apiService
					._ensureSession()
					.then(() => {
						this._refreshingSession = false;
						if (this.apiService.sessionToken) {
							this._initWebSocket();
						}
					})
					.catch(() => {
						this._refreshingSession = false;
					});
			})
		);

		this.wsService.connect();
	}

	open() {
		this.LiveChatState.setOpen(true);
	}

	close() {
		this.LiveChatState.setOpen(false);
	}

	toggle() {
		this.LiveChatState.setOpen(!this.LiveChatState.isOpen);
	}

	navigateTo(view) {
		this.LiveChatState.setView(view);
		if (!this.LiveChatState.isOpen) {
			this.open();
		}
	}

	setConversations(conversations) {
		this.LiveChatState.setConversations(conversations);
	}

	addMessage(conversationId, message) {
		this.LiveChatState.addMessage(conversationId, message);
	}

	setHelpArticles(articles) {
		this.LiveChatState.setHelpArticles(articles);
	}

	setHomeChangelogItems(items) {
		this.LiveChatState.setHomeChangelogItems(items);
	}

	setChangelogItems(items) {
		this.LiveChatState.setChangelogItems(items);
	}

	setUnreadCount(count) {
		this.LiveChatState.unreadCount = count;
		this.LiveChatState._notify('unreadCountChange', { count });
	}

	getState() {
		return {
			isOpen: this.LiveChatState.isOpen,
			currentView: this.LiveChatState.currentView,
			unreadCount: this.LiveChatState.unreadCount,
			conversations: this.LiveChatState.conversations,
		};
	}

	_applyPreviewData() {
		const data = this.liveChatOptions.previewData;
		if (!data || typeof data !== 'object') {
			return;
		}

		if (Array.isArray(data.conversations)) {
			this.LiveChatState.setConversations(data.conversations);
		}

		if (Array.isArray(data.helpArticles)) {
			this.LiveChatState.setHelpArticles(data.helpArticles);
		}

		if (Array.isArray(data.homeChangelogItems)) {
			this.LiveChatState.setHomeChangelogItems(data.homeChangelogItems);
		}

		if (Array.isArray(data.changelogItems)) {
			this.LiveChatState.setChangelogItems(data.changelogItems);
		}

		if (typeof data.unreadCount === 'number') {
			this.setUnreadCount(data.unreadCount);
		}

		if (data.availability && typeof data.availability === 'object') {
			const availability = data.availability;
			this.LiveChatState.agentsOnline = Boolean(
				availability.agentsOnline ??
					availability.agents_online ??
					availability.is_online
			);
			this.LiveChatState.onlineCount =
				availability.onlineCount ?? availability.online_count ?? 0;
			this.LiveChatState.responseTime =
				availability.responseTime ??
				availability.response_time ??
				this.LiveChatState.responseTime;
			this.LiveChatState._notify('availabilityUpdate', availability);
		}

		if (typeof data.currentView === 'string') {
			this.LiveChatState.setView(data.currentView);
		}
	}

	async loadInitialData() {
		try {
			const conversations = await this._fetchConversations();
			this.LiveChatState.setConversations(conversations);
		} catch (error) {
			console.error('[LiveChatWidget] Failed to load conversations:', error);
		}

		if (this.liveChatOptions.enableHelp) {
			try {
				const articles = await this._fetchHelpArticles();
				this.LiveChatState.setHelpArticles(articles);
			} catch (error) {
				console.error('[LiveChatWidget] Failed to load help articles:', error);
			}
		}

		if (this.liveChatOptions.enableChangelog) {
			try {
				const { homeItems, changelogItems } = await this._fetchChangelog();
				this.LiveChatState.setHomeChangelogItems(homeItems);
				this.LiveChatState.setChangelogItems(changelogItems);
			} catch (error) {
				console.error('[LiveChatWidget] Failed to load changelog:', error);
			}
		}
	}

	async _fetchConversations() {
		try {
			const response = await this.apiService.getConversations();
			if (response.status && response.data) {
				return response.data.map((conv) => ({
					id: conv.id,
					title:
						conv.subject ||
						`Chat with ${conv.assigned_user?.name || 'Support'}`,
					participants: conv.assigned_user
						? [
								{
									name: conv.assigned_user.name,
									avatarUrl: conv.assigned_user.avatar,
								},
							]
						: [{ name: 'Support', avatarUrl: null }],
					lastMessage: conv.preview || conv.snippet || '',
					lastMessageTime: conv.last_message_at,
					unread: conv.unread || 0,
					status: conv.status,
				}));
			}
			return [];
		} catch (error) {
			console.error('[LiveChatWidget] Failed to fetch conversations:', error);
			return [];
		}
	}

	async _fetchHelpArticles() {
		try {
			const response = await this.apiService.getHelpCollections();
			if (response.success && response.data) {
				const collections = response.data.collections || response.data;
				const helpBase = (this.liveChatOptions.helpUrl || '').replace(
					/\/$/,
					''
				);

				const sorted = [...collections].sort(
					(a, b) => (a.order ?? 0) - (b.order ?? 0)
				);

				return sorted.map((collection) => ({
					id: collection.id,
					title: collection.title,
					description: collection.description || '',
					articleCount: collection.article_count || 0,
					icon: collection.icon || null,
					author: collection.author || null,
					url:
						collection.url_slug && helpBase
							? `${helpBase}/collections/${collection.url_slug}`
							: helpBase || null,
				}));
			}
			return [];
		} catch (error) {
			console.error('[LiveChatWidget] Failed to fetch help articles:', error);
			return [];
		}
	}

	async fetchMessages(conversationId) {
		try {
			const response = await this.apiService.getConversation(conversationId);
			if (response.status && response.data) {
				const messages = (response.data.messages || []).map((msg) => {
					let attachments;
					if (msg.attachments) {
						try {
							attachments =
								typeof msg.attachments === 'string'
									? JSON.parse(msg.attachments)
									: msg.attachments;
						} catch (e) {
							// ignore
						}
					}
					return {
						id: msg.id,
						content: msg.content,
						isOwn: msg.sender_type === 'customer',
						isSystem: msg.sender_type === 'system',
						timestamp: msg.created_at,
						attachments:
							attachments && attachments.length > 0 ? attachments : undefined,
						sender: {
							name:
								msg.sender_name ||
								(msg.sender_type === 'customer' ? 'You' : 'Support'),
							avatarUrl: msg.sender_avatar || null,
						},
					};
				});
				this.LiveChatState.setMessages(conversationId, messages);

				await this.apiService.markConversationAsRead(conversationId);
				this.LiveChatState.markAsRead(conversationId);

				return messages;
			}
			return [];
		} catch (error) {
			console.error('[LiveChatWidget] Failed to fetch messages:', error);
			return [];
		}
	}

	async startNewConversation(message, subject = '', pendingAttachments = []) {
		try {
			const uploadedAttachments =
				await this._uploadPendingAttachments(pendingAttachments);

			const response = await this.apiService.startConversation({
				message,
				subject,
				attachments: uploadedAttachments,
			});

			if (response.status && response.data) {
				const conv = response.data;
				const newConversation = {
					id: conv.id,
					title: conv.subject || 'New conversation',
					participants: [{ name: 'Support', avatarUrl: null }],
					lastMessage: message,
					lastMessageTime: conv.created_at || new Date().toISOString(),
					unread: 0,
					status: 'open',
				};

				this.LiveChatState.addConversation(newConversation);

				// Optimistic: show the user's message immediately so the chat
				// view isn't empty during the next request. fetchMessages
				// below replaces this with the authoritative server state,
				// which includes the backend's auto-response (bot reply).
				this.LiveChatState.setMessages(conv.id, [
					{
						id: 'msg_optimistic_' + Date.now(),
						content: message,
						isOwn: true,
						timestamp: new Date().toISOString(),
					},
				]);

				this.LiveChatState.setActiveConversation(conv.id);
				this.LiveChatState.setView('chat');

				// Fetch the conversation right after creation to pick up the
				// server-generated bot auto-response message. Fire-and-forget
				// — fetchMessages updates state when it returns.
				this.fetchMessages(conv.id);

				return conv;
			}
			return null;
		} catch (error) {
			console.error('[LiveChatWidget] Failed to start conversation:', error);
			return null;
		}
	}

	async sendTypingIndicator(conversationId, isTyping) {
		if (this.LiveChatState.supportBlocked) return;
		try {
			await this.apiService.sendTypingIndicator(conversationId, isTyping);
		} catch (error) {
			// silent fail
		}
	}

	async _fetchAndApplySettings() {
		try {
			const response = await this.apiService.getLiveChatSettings();
			if (!response?.status || !response?.data) return;

			const s = response.data;
			let stylesChanged = false;

			// Team info
			if (s.team_name && !this._hasExplicitOption('teamName')) {
				this.liveChatOptions.teamName = s.team_name;
				this.LiveChatState.teamName = s.team_name;
			}
			if (s.logo_url && !this._hasExplicitOption('logoUrl')) {
				this.liveChatOptions.logoUrl = s.logo_url;
			}
			if (s.greeting_message && !this._hasExplicitOption('greetingMessage')) {
				this.LiveChatState.greetingMessage = s.greeting_message;
			}
			if (s.welcome_message && !this._hasExplicitOption('welcomeMessage')) {
				this.LiveChatState.welcomeMessage = s.welcome_message;
			}
			if (s.response_time && !this._hasExplicitOption('responseTime')) {
				this.LiveChatState.responseTime = s.response_time;
			}
			if (
				Array.isArray(s.team_avatars) &&
				s.team_avatars.length > 0 &&
				!this._hasExplicitOption('teamAvatars')
			) {
				this.LiveChatState.teamAvatars = s.team_avatars;
			}

			// Colors / theme
			if (s.primary_color && !this._hasExplicitOption('primaryColor')) {
				this.liveChatOptions.primaryColor = s.primary_color;
				stylesChanged = true;
			}
			if (s.background_color && !this._hasExplicitOption('backgroundColor')) {
				this.liveChatOptions.backgroundColor = s.background_color;
				stylesChanged = true;
			}
			if (s.text_color && !this._hasExplicitOption('textColor')) {
				this.liveChatOptions.textColor = s.text_color;
				stylesChanged = true;
			}
			let themeChanged = false;
			if (
				s.theme &&
				!this._hasExplicitOption('theme') &&
				s.theme !== this.liveChatOptions.theme
			) {
				this.liveChatOptions.theme = s.theme;
				stylesChanged = true;
				themeChanged = true;
			}
			if (stylesChanged) {
				applyliveChatCustomStyles({
					primaryColor: this.liveChatOptions.primaryColor,
					textColor: this.liveChatOptions.textColor,
					backgroundColor: this.liveChatOptions.backgroundColor,
					theme: this.liveChatOptions.theme,
				});
			}
			if (themeChanged && this._widgetContainer) {
				this._widgetContainer.className = `liveChat-widget theme-${this.liveChatOptions.theme}`;
			}

			// Launcher position & padding
			let positionChanged = false;
			if (s.launcher_position && !this._hasExplicitOption('position')) {
				const parts = s.launcher_position.split('-');
				const newPosition = parts[parts.length - 1] || 'right';
				if (newPosition !== this.liveChatOptions.position) {
					this.liveChatOptions.position = newPosition;
					positionChanged = true;
				}
			}
			if (positionChanged) {
				if (this.launcher?.element) {
					this.launcher.element.className = `liveChat-launcher liveChat-launcher-${this.liveChatOptions.position}`;
					this.launcher.options.position = this.liveChatOptions.position;
				}
				if (this.panel?.element) {
					const wasOpen = this.panel.element.classList.contains('open');
					this.panel.element.className = `liveChat-panel liveChat-panel-${this.liveChatOptions.position}${wasOpen ? ' open' : ''}`;
					this.panel.options.position = this.liveChatOptions.position;
				}
			}
			if (
				typeof s.horizontal_padding === 'number' &&
				!this._hasExplicitOption('horizontalPadding')
			) {
				this.liveChatOptions.horizontalPadding = s.horizontal_padding;
			}
			if (
				typeof s.vertical_padding === 'number' &&
				!this._hasExplicitOption('verticalPadding')
			) {
				this.liveChatOptions.verticalPadding = s.vertical_padding;
			}
			if (this._widgetContainer) {
				this._widgetContainer.style.setProperty(
					'--liveChat-h-padding',
					`${this.liveChatOptions.horizontalPadding ?? 20}px`
				);
				this._widgetContainer.style.setProperty(
					'--liveChat-v-padding',
					`${this.liveChatOptions.verticalPadding ?? 20}px`
				);
			}

			// Module visibility
			if (
				typeof s.home_module_enabled === 'boolean' &&
				!this._hasExplicitOption('homeModuleEnabled')
			) {
				this.LiveChatState.homeModuleEnabled = s.home_module_enabled;
				if (
					!s.home_module_enabled &&
					this.LiveChatState.currentView === 'home'
				) {
					this.LiveChatState.currentView =
						this.LiveChatState.messagesModuleEnabled !== false
							? 'messages'
							: 'help';
				}
			}
			if (
				typeof s.messages_module_enabled === 'boolean' &&
				!this._hasExplicitOption('messagesModuleEnabled')
			) {
				this.LiveChatState.messagesModuleEnabled = s.messages_module_enabled;
				if (
					!s.messages_module_enabled &&
					this.LiveChatState.currentView === 'messages'
				) {
					this.LiveChatState.currentView =
						this.LiveChatState.homeModuleEnabled !== false ? 'home' : 'help';
				}
			}
			if (
				typeof s.help_module_enabled === 'boolean' &&
				!this._hasExplicitOption('enableHelp')
			) {
				this.LiveChatState.enableHelp = s.help_module_enabled;
				if (
					!s.help_module_enabled &&
					this.LiveChatState.currentView === 'help'
				) {
					this.LiveChatState.currentView =
						this.LiveChatState.homeModuleEnabled !== false
							? 'home'
							: 'messages';
				}
			}
			if (
				typeof s.changelog_module_enabled === 'boolean' &&
				!this._hasExplicitOption('enableChangelog')
			) {
				this.LiveChatState.enableChangelog = s.changelog_module_enabled;
				if (
					!s.changelog_module_enabled &&
					this.LiveChatState.currentView === 'changelog'
				) {
					this.LiveChatState.currentView =
						this.LiveChatState.homeModuleEnabled !== false
							? 'home'
							: 'messages';
				}
			}

			// Feature flags
			if (
				typeof s.show_avatars === 'boolean' &&
				!this._hasExplicitOption('showAvatars')
			) {
				this.LiveChatState.showAvatars = s.show_avatars;
			}
			if (s.start_button_text && !this._hasExplicitOption('startButtonText')) {
				this.LiveChatState.startButtonText = s.start_button_text;
			}
			if (
				typeof s.require_email_before_chat === 'boolean' &&
				!this._hasExplicitOption('requireEmailBeforeChat')
			) {
				this.LiveChatState.requireEmailBeforeChat = s.require_email_before_chat;
			}
			if (
				typeof s.allow_anonymous_chat === 'boolean' &&
				!this._hasExplicitOption('allowAnonymousChat')
			) {
				this.LiveChatState.allowAnonymousChat = s.allow_anonymous_chat;
			}
			if (
				typeof s.allow_attachments === 'boolean' &&
				!this._hasExplicitOption('allowAttachments')
			) {
				this.LiveChatState.allowAttachments = s.allow_attachments;
			}
			if (
				typeof s.allow_emoji === 'boolean' &&
				!this._hasExplicitOption('allowEmoji')
			) {
				this.LiveChatState.allowEmoji = s.allow_emoji;
			}
			if (
				typeof s.show_reply_time === 'boolean' &&
				!this._hasExplicitOption('showReplyTime')
			) {
				this.LiveChatState.showReplyTime = s.show_reply_time;
			}

			// URLs
			if (s.feedback_url && !this._hasExplicitOption('feedbackUrl')) {
				this.liveChatOptions.feedbackUrl = s.feedback_url;
				this.LiveChatState.urls.feedback = s.feedback_url;
			}
			if (s.help_url && !this._hasExplicitOption('helpUrl')) {
				this.liveChatOptions.helpUrl = s.help_url;
				this.LiveChatState.urls.help = s.help_url;
			}
			if (s.changelog_url && !this._hasExplicitOption('changelogUrl')) {
				this.liveChatOptions.changelogUrl = s.changelog_url;
				this.LiveChatState.urls.changelog = s.changelog_url;
			}
			if (s.roadmap_url && !this._hasExplicitOption('roadmapUrl')) {
				this.liveChatOptions.roadmapUrl = s.roadmap_url;
				this.LiveChatState.urls.roadmap = s.roadmap_url;
			}

			// Business hours availability
			if (s.availability && typeof s.availability === 'object') {
				this.LiveChatState.businessHoursState = s.availability.state || null;
				this.LiveChatState.nextOpenAt = s.availability.next_open_at || null;
				this.LiveChatState.holidayName = s.availability.holiday_name || null;
				this.LiveChatState.holidayDate = s.availability.holiday_date || null;
			}

			this.LiveChatState._notify('settingsUpdate', {});
		} catch (e) {
			console.warn('[LiveChat] Failed to fetch/apply settings:', e);
		}
	}

	_hasExplicitOption(key) {
		return Object.prototype.hasOwnProperty.call(
			this._explicitOptions || {},
			key
		);
	}

	async checkAgentAvailability() {
		try {
			const response = await this.apiService.checkAgentsOnline();
			if (response.status && response.data) {
				this.LiveChatState.agentsOnline =
					response.data.agents_online ?? response.data.is_online ?? false;
				this.LiveChatState.onlineCount = response.data.online_count || 0;
				this.LiveChatState.responseTime = response.data.response_time || '';

				if (response.data.available_agents && this.LiveChatState.showAvatars !== false) {
					this.LiveChatState.setTeamAvatarsFromAgents(
						response.data.available_agents
					);
				}

				this.LiveChatState._notify('availabilityUpdate', response.data);
				return response.data;
			}
			return { agents_online: false, online_count: 0 };
		} catch (error) {
			console.error('[LiveChatWidget] Failed to check availability:', error);
			return { agents_online: false, online_count: 0 };
		}
	}

	async _fetchChangelog() {
		if (this.apiService?.mock) {
			return {
				homeItems: [
					{
						id: 'changelog_5',
						title: 'New integrations available',
						description:
							'Connect with more tools you love and streamline your workflow.',
						labels: [{ name: 'Integration', color: '#dbeafe' }],
						coverImage:
							'https://images.unsplash.com/photo-1674027444485-cec3da58eef4?w=500&auto=format&fit=crop&q=60',
						publishedAt: new Date(
							Date.now() - 14 * 24 * 60 * 60 * 1000
						).toISOString(),
						url: '#',
					},
					{
						id: 'changelog_2',
						title: 'A new era of Insights has arrived',
						description:
							'We announced Fin Insights, a groundbreaking, AI-powered product that gives you complete visibility into every customer conversation.',
						labels: [
							{ name: 'New feature', color: '#dcfce7' },
							{ name: 'AI', color: '#f3e8ff' },
						],
						coverImage:
							'https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?w=500&auto=format&fit=crop&q=60',
						publishedAt: new Date(
							Date.now() - 5 * 24 * 60 * 60 * 1000
						).toISOString(),
						url: '#',
					},
					{
						id: 'changelog_1',
						title: 'The 2025 Customer Service Transformation Report',
						description:
							'Learn how AI has transformed customer service from the ground up—rewriting its economics and reshaping expectations.',
						labels: [{ name: 'Report', color: '#fef9c3' }],
						coverImage:
							'https://images.unsplash.com/photo-1762330467475-a565d04e1808?w=500&auto=format&fit=crop&q=60',
						publishedAt: new Date(
							Date.now() - 2 * 24 * 60 * 60 * 1000
						).toISOString(),
						url: '#',
					},
				],
				changelogItems: [
					{
						id: 'changelog_4',
						title: 'Enhanced conversation analytics',
						description:
							'Get deeper insights into your customer conversations with our new analytics dashboard.',
						labels: [{ name: 'Analytics', color: '#fce7f3' }],
						coverImage:
							'https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?w=500&auto=format&fit=crop&q=60',
						publishedAt: new Date(
							Date.now() - 10 * 24 * 60 * 60 * 1000
						).toISOString(),
						url: '#',
					},
					{
						id: 'changelog_3',
						title: 'Escalation guidance for complex issues',
						description:
							'New AI-powered escalation guidance helps your team handle complex customer issues more effectively.',
						labels: [
							{ name: 'New feature', color: '#dcfce7' },
							{ name: 'AI', color: '#f3e8ff' },
						],
						coverImage:
							'https://images.unsplash.com/photo-1764773516703-b246ac2ad5ef?w=500&auto=format&fit=crop&q=60',
						publishedAt: new Date(
							Date.now() - 7 * 24 * 60 * 60 * 1000
						).toISOString(),
						url: '#',
					},
				],
			};
		}

		try {
			const response = await this.apiService.getChangelogs({ limit: 20 });

			if (response.success && response.data) {
				const changelogs = Array.isArray(response.data) ? response.data : [];
				const changelogBase = (this.liveChatOptions.changelogUrl || '').replace(
					/\/$/,
					''
				);

				const mappedItems = changelogs.map((item) => ({
					id: item.id,
					title: item.title,
					description: item.excerpt || item.description || '',
					labels: item.labels || [],
					coverImage: item.cover_image || null,
					publishedAt: item.published_at,
					url:
						item.slug && changelogBase
							? `${changelogBase}/${item.slug}`
							: changelogBase || null,
				}));

				return {
					homeItems: mappedItems.slice(0, 3),
					changelogItems: mappedItems,
				};
			}

			return { homeItems: [], changelogItems: [] };
		} catch (error) {
			console.error('[LiveChatWidget] Failed to fetch changelog:', error);
			return { homeItems: [], changelogItems: [] };
		}
	}

	async onMount() {
		this._applyPreviewData();

		if (this.liveChatOptions.autoLoadData) {
			// Fetch workspace settings and apply only if not explicitly configured
			this._fetchAndApplySettings();

			this.loadInitialData();

			if (this.apiService?.sessionToken) {
				this._initWebSocket();
			}

			this.checkAgentAvailability();

			this._availabilityInterval = setInterval(() => {
				this.checkAgentAvailability();
			}, 60000);
		}

		if (
			this.liveChatOptions.initialView &&
			this.liveChatOptions.initialView !== this.LiveChatState.currentView
		) {
			this.LiveChatState.setView(this.liveChatOptions.initialView);
		}
	}

	onDestroy() {
		if (this._stateUnsubscribe) {
			this._stateUnsubscribe();
		}

		if (this.wsService) {
			this.wsService.disconnect();
		}

		this._wsUnsubscribers.forEach((unsub) => unsub());
		this._wsUnsubscribers = [];

		if (this._availabilityInterval) {
			clearInterval(this._availabilityInterval);
		}
	}

	destroy() {
		removeliveChatCustomStyles();

		if (this.launcher) {
			this.launcher.destroy();
		}
		if (this.panel) {
			this.panel.destroy();
		}
		this.onDestroy();
		super.destroy();
	}
}
